/**
 * A project's Overview tab for staff (MB's R246, in place of the Progress tab;
 * docs/specs/project-reporting/overview-everywhere.md): Teslyn Field's Project overview, its sections, order, figures
 * and Day, Week, Month and Project reviews, without the quick-start checklist. Every figure, word and column comes from
 * the domain's `projectOverview`; this draws it with Teslyn Cloud's tables (`TableWrap`: cards on a phone or a narrow
 * iPad, decision 1026). A client's tab stays Progress (`ProgressPanel`): none of this reaches a client.
 */
import { useCallback, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  aircraftCells,
  blockCells,
  dailyCsv,
  dayCells,
  dayTotalCells,
  OVERVIEW_NO_BLOCKS,
  OVERVIEW_NO_ENTRIES,
  OVERVIEW_NO_PILOTS,
  OVERVIEW_QC_UNKNOWN_WORDS,
  OVERVIEW_SOURCE_WORDS,
  overviewNotHere,
  periodCells,
  pilotCells,
  REMAINING_KM_TITLE,
  REVIEW_KIND_LABEL,
  type OverviewCell,
  type OverviewChip,
  type OverviewDay,
  type OverviewPeriod,
  type OverviewTile,
  type ProjectOverview,
  type ReviewKind,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField } from '../components/Fields.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { TableWrap } from '../components/TableWrap.tsx';
import { TabPanel, Tabs } from '../components/Tabs.tsx';
import type { PortalProject, ProjectPlan } from './api.ts';
import { loadOverview, type CloudOverview } from './overviewApi.ts';

const CELL_CLASS: Readonly<Record<OverviewCell['kind'], string | undefined>> = {
  figure: 'data num',
  data: 'data',
  text: undefined,
};

const Heads = ({ cells, more }: { readonly cells: readonly OverviewCell[]; readonly more?: ReactNode }) => (
  <tr>
    {cells.map((c) => (
      <th key={c.label} className={c.kind === 'figure' ? 'label num' : 'label'} scope="col">
        {c.label}
      </th>
    ))}
    {more}
  </tr>
);

const Cells = ({ cells }: { readonly cells: readonly OverviewCell[] }) => (
  <>
    {cells.map((c) => (
      <td key={c.label} className={CELL_CLASS[c.kind]}>
        {c.text}
      </td>
    ))}
  </>
);

const CHIP_CLASS: Readonly<Record<OverviewChip['state'], string>> = {
  pass: 'chip chip--pass label',
  warn: 'chip chip--warn label',
  fail: 'chip chip--fail label',
  pending: 'chip label',
};

const Chip = ({ chip }: { readonly chip: OverviewChip }) => (
  <span className={CHIP_CLASS[chip.state]}>{chip.word}</span>
);

function Tile({ tile }: { readonly tile: OverviewTile }): ReactElement {
  const fraction = Math.max(0, Math.min(1, tile.progress?.fraction ?? 0));
  return (
    <li className="metric-tile" title={tile.hint?.join('\n')}>
      <span className="label muted">{tile.label}</span>
      <span className="metric metric-tile__value">
        {tile.value}
        {tile.unit && <span className="metric-tile__unit body muted"> {tile.unit}</span>}
      </span>
      {tile.progress && (
        <>
          <span
            className="ops__bar"
            role="progressbar"
            aria-label={tile.label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(fraction * 100)}
          >
            <span style={{ width: `${fraction * 100}%` }} />
          </span>
          <span className="caption muted">{tile.progress.text}</span>
        </>
      )}
      {tile.status && <span className="caption muted">{tile.status}</span>}
    </li>
  );
}

function BlocksTable({ overview }: { readonly overview: ProjectOverview }): ReactElement {
  const first = overview.blocks[0];
  return (
    <TableWrap>
      <table className="table" aria-label="Blocks">
        <thead>
          {first && (
            <tr>
              {blockCells(first).map((c) => (
                <th
                  key={c.label}
                  className={c.kind === 'figure' ? 'label num' : 'label'}
                  scope="col"
                  title={c.label === 'Remaining km' ? REMAINING_KM_TITLE : undefined}
                >
                  {c.label}
                </th>
              ))}
              <th className="label" scope="col">
                Status
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {overview.blocks.map((b) => (
            <tr key={b.id}>
              <Cells cells={blockCells(b)} />
              <td>
                <Chip chip={b.chip} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

const NoEntries = () => (
  <div className="panel">
    <p className="body">{OVERVIEW_NO_ENTRIES.title}</p>
    <p className="body muted">{OVERVIEW_NO_ENTRIES.body}</p>
  </div>
);

function DayReview({
  days,
  onExport,
}: {
  readonly days: readonly OverviewDay[];
  readonly onExport: () => void;
}): ReactElement {
  const [picked, setPicked] = useState<string | null>(null);
  const day = days.find((d) => d.date === picked) ?? days[0];
  if (!day) return <NoEntries />;
  const first = day.rows[0];
  return (
    <>
      <div className="row row--wrap">
        <SelectField<string>
          label="Day"
          value={day.date}
          onChange={setPicked}
          options={days.map((d) => [d.date, d.date] as const)}
        />
        <span className="caption muted">{day.summary}</span>
        <Button onClick={onExport}>Export CSV</Button>
      </div>
      <TableWrap>
        <table className="table" aria-label="Day view">
          <thead>{first && <Heads cells={dayCells(first)} />}</thead>
          <tbody>
            {day.rows.map((r) => (
              <tr key={r.id}>
                <Cells cells={dayCells(r)} />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={5}>Day total</th>
              <Cells cells={dayTotalCells(day.totals)} />
            </tr>
          </tfoot>
        </table>
      </TableWrap>
    </>
  );
}

const TOTAL_WORD: Readonly<Record<ReviewKind, string>> = {
  week: 'Week total',
  month: 'Month total',
  project: 'Project total',
};

function PeriodReview({
  periods,
  kind,
}: {
  readonly periods: readonly OverviewPeriod[];
  readonly kind: ReviewKind;
}): ReactElement {
  const [picked, setPicked] = useState<string | null>(null);
  const period = periods.find((p) => p.key === picked) ?? periods[0];
  if (!period) return <NoEntries />;
  const heads = periodCells(period.totals, period.suspended);
  return (
    <>
      <div className="row row--wrap">
        {kind === 'project' ? (
          <span className="label muted">
            {period.from} to {period.to}
          </span>
        ) : (
          <SelectField<string>
            label={kind === 'week' ? 'Week' : 'Month'}
            value={period.key}
            onChange={setPicked}
            options={periods.map((p) => [p.key, p.label] as const)}
          />
        )}
        <span className="caption muted">{period.summary}</span>
      </div>
      <TableWrap>
        <table className="table" aria-label={REVIEW_KIND_LABEL[kind]}>
          <thead>
            <tr>
              <th className="label" scope="col">
                {kind === 'project' ? 'Week' : 'Day'}
              </th>
              {heads.map((c) => (
                <th key={c.label} className="label num" scope="col">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {period.rows.map((r) => (
              <tr key={r.key}>
                <td className="data">{r.label}</td>
                <Cells cells={periodCells(r.totals, period.suspended)} />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>{TOTAL_WORD[kind]}</th>
              <Cells cells={heads} />
            </tr>
          </tfoot>
        </table>
      </TableWrap>
    </>
  );
}

type ReviewTab = 'day' | ReviewKind;
const REVIEW_TABS: ReadonlyArray<readonly [ReviewTab, string]> = [
  ['day', REVIEW_KIND_LABEL.day],
  ['week', REVIEW_KIND_LABEL.week],
  ['month', REVIEW_KIND_LABEL.month],
  ['project', REVIEW_KIND_LABEL.project],
];

function Reviews({
  overview,
  onExport,
}: {
  readonly overview: ProjectOverview;
  readonly onExport: () => void;
}): ReactElement {
  const [tab, setTab] = useState<ReviewTab>('day');
  return (
    <>
      <Tabs label="Reviews" tabs={REVIEW_TABS} current={tab} onChange={setTab} idPrefix="review" />
      <TabPanel idPrefix="review" current={tab}>
        {tab === 'day' ? (
          <DayReview days={overview.days} onExport={onExport} />
        ) : (
          <PeriodReview key={tab} periods={overview.reviews[tab]} kind={tab} />
        )}
      </TabPanel>
    </>
  );
}

function AircraftTable({ overview }: { readonly overview: ProjectOverview }): ReactElement {
  const first = overview.aircraft[0];
  if (!first)
    return (
      <div className="panel">
        <p className="body">No aircraft yet. Add the survey aircraft on the Aircraft page.</p>
      </div>
    );
  return (
    <TableWrap>
      <table className="table" aria-label="Aircraft hours and maintenance release">
        <thead>
          <Heads
            cells={aircraftCells(first)}
            more={
              <>
                <th className="label" scope="col">
                  MR
                </th>
                <th className="label" scope="col">
                  MR history (TTIS h)
                </th>
              </>
            }
          />
        </thead>
        <tbody>
          {overview.aircraft.map((a) => (
            <tr key={a.id}>
              <Cells cells={aircraftCells(a)} />
              <td>
                <Chip chip={a.mr} />
              </td>
              <td className="caption">{a.history}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function PilotsTable({ overview }: { readonly overview: ProjectOverview }): ReactElement {
  const first = overview.pilots[0];
  if (!first)
    return (
      <div className="panel">
        <p className="body">{OVERVIEW_NO_PILOTS.title}</p>
        <p className="body muted">{OVERVIEW_NO_PILOTS.body}</p>
      </div>
    );
  return (
    <TableWrap>
      <table className="table" aria-label="Pilots">
        <thead>
          <Heads cells={pilotCells(first)} />
        </thead>
        <tbody>
          {overview.pilots.map((p) => (
            <tr key={p.id}>
              <Cells cells={pilotCells(p)} />
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

/** Offer the daily columns as a download, the file Teslyn Field's "Export CSV" writes. */
function saveCsv(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function OverviewPanel({
  sb,
  project,
  plan,
  onOpenFlights,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly plan: ProjectPlan;
  /** "Open Flights" beside the QC line: the Flights tab has each flight's Actioned state (decision 751). */
  readonly onOpenFlights: () => void;
}): ReactElement {
  const [data, setData] = useState<CloudOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    loadOverview(sb, project, plan)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, project, plan, attempt]);
  const exportCsv = useCallback(() => {
    if (data) saveCsv(`${project.number}_Daily_Columns.csv`, dailyCsv(data.csv));
  }, [data, project.number]);

  if (error) return <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data) return <Loading what="project summary" />;
  const o = data.overview;
  return (
    <div className="stack" aria-label="Project overview">
      {o.qcPending === null && (
        <div className="notice notice--warn" role="status">
          <p className="body">{OVERVIEW_QC_UNKNOWN_WORDS}</p>
        </div>
      )}
      {o.qcPending !== null && o.qcPending > 0 && (
        <div className="notice" role="note" aria-label="QC actions pending">
          <p className="body">
            QC actions pending: {o.qcPending} flight{o.qcPending === 1 ? '' : 's'}
          </p>
          <div className="row">
            <Button onClick={onOpenFlights}>Open Flights</Button>
          </div>
        </div>
      )}
      {o.blocks.length === 0 ? (
        <div className="panel">
          <p className="body">{OVERVIEW_NO_BLOCKS.title}</p>
          <p className="body muted">{OVERVIEW_NO_BLOCKS.body}</p>
        </div>
      ) : (
        <>
          <ul className="metric-tiles overview__tiles" aria-label="Summary">
            {o.tiles.map((t) => (
              <Tile key={t.label} tile={t} />
            ))}
          </ul>
          <BlocksTable overview={o} />
        </>
      )}
      <h2 className="heading">Reviews</h2>
      <Reviews overview={o} onExport={exportCsv} />
      <h2 className="heading">Aircraft</h2>
      <AircraftTable overview={o} />
      <h2 className="heading">Pilots</h2>
      <PilotsTable overview={o} />
      <p className="caption muted">{OVERVIEW_SOURCE_WORDS}</p>
      <ul className="caption muted" aria-label="In Teslyn Field only">
        {overviewNotHere('cloud').map((n) => (
          <li key={n.item}>
            {n.item}: not in Teslyn Cloud. {n.reason}
          </li>
        ))}
      </ul>
    </div>
  );
}
